import { Contract } from '../../../types/compliance';
import { isMonthly, isMonthToMonth } from './contractTerm';

/** How far ahead (days) something counts as needing attention. */
export const ATTENTION_WINDOW_DAYS = 30;

const OPEN_STATUSES = new Set(['draft', 'active', 'pending_renewal']);

export interface AttentionItem {
  contract: Contract;
  kind: 'overdue' | 'due';
  /** YYYY-MM-DD the item is about. */
  date: string;
  /** Short lead-in shown before the date, e.g. "Give notice by". */
  reason: string;
}

/** YYYY-MM-DD shifted by `days`, computed in UTC so time zones can't move it. */
export function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Today's local calendar date as YYYY-MM-DD. */
export function localToday(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * What needs this user's attention: open contracts they own (or are renewal
 * owner on) with a deadline passed or coming up within 30 days. One line per
 * contract (its most urgent item), overdue first, then soonest.
 * Dates are YYYY-MM-DD strings, which compare correctly as text.
 */
export function attentionItems(
  contracts: Contract[],
  userOid: string,
  today: string = localToday(),
): AttentionItem[] {
  if (!userOid) return [];
  const horizon = addDays(today, ATTENTION_WINDOW_DAYS);
  const items: AttentionItem[] = [];

  for (const c of contracts) {
    if (c.is_deleted || !OPEN_STATUSES.has(c.status)) continue;
    if (c.owner_oid !== userOid && c.renewal_owner_oid !== userOid) continue;
    // Month-to-month contracts roll over every month; the row highlight covers them.
    if (isMonthToMonth(c)) continue;
    const stillRunning = c.end_date >= today;
    const candidates: AttentionItem[] = [];

    if (c.auto_renewal && c.notice_period_days !== null && c.notice_period_days !== undefined) {
      const deadline = addDays(c.end_date, -c.notice_period_days);
      if (deadline < today && stillRunning) {
        candidates.push({ contract: c, kind: 'overdue', date: deadline, reason: 'Notice deadline passed' });
      } else if (deadline >= today && deadline <= horizon) {
        candidates.push({ contract: c, kind: 'due', date: deadline, reason: 'Give notice by' });
      }
    }
    if (c.renewal_action_date) {
      const action = c.renewal_action_date;
      if (action < today && stillRunning) {
        candidates.push({ contract: c, kind: 'overdue', date: action, reason: 'Renewal action was due' });
      } else if (action >= today && action <= horizon) {
        candidates.push({ contract: c, kind: 'due', date: action, reason: 'Renewal action due' });
      }
    }
    if (!c.auto_renewal && stillRunning && c.end_date <= horizon) {
      candidates.push({ contract: c, kind: 'due', date: c.end_date, reason: 'Ends' });
    }

    candidates.sort(compareItems);
    if (candidates[0]) items.push(candidates[0]);
  }
  return items.sort(compareItems);
}

/**
 * How the Contracts list highlights this row:
 * - 'monthly' (gray): any contract with a 1-month Contract Length, whatever
 *   its status or end date. It always ends within a month, so "expiring"
 *   would be noise.
 * - 'expiring' (amber): any other open contract whose End Date is within 30
 *   days or already past.
 */
export type RowHighlight = 'monthly' | 'expiring' | null;

export function rowHighlight(c: Contract, today: string = localToday()): RowHighlight {
  if (isMonthly(c)) return 'monthly';
  if (!OPEN_STATUSES.has(c.status)) return null;
  return c.end_date <= addDays(today, ATTENTION_WINDOW_DAYS) ? 'expiring' : null;
}

function compareItems(a: AttentionItem, b: AttentionItem): number {
  if (a.kind !== b.kind) return a.kind === 'overdue' ? -1 : 1;
  return a.date.localeCompare(b.date);
}

/** "Oct 3, 2026" for a YYYY-MM-DD string, without time-zone drift. */
export function formatDateOnly(isoDate: string | null | undefined): string {
  if (!isoDate) return '-';
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
